/**
 * @file authService.js
 * @description Authentifizierungs-Service für die Benutzeranmeldung in ThekenOS.
 *
 * Wie der Login funktioniert:
 * Thekenkräfte melden sich mit einem einfachen Benutzernamen und Passwort an (statt einer E-Mail-Adresse).
 * Um das abzusichern, leitet dieser Service die Anmeldedaten an die Edge Function `auth-login` weiter.
 * Dort wird:
 * 1. Der Benutzername serverseitig in die interne Supabase-E-Mail-Adresse aufgelöst (der Browser erfährt diese nie).
 * 2. Das Passwort verifiziert.
 * 3. Falls aktiv, ein Bot-Schutz (CAPTCHA / Turnstile) geprüft.
 * 4. Bei Erfolg werden Sicherheitstoken (Access- und Refresh-Token) an das Frontend zurückgegeben
 *    und in den Supabase-Client geladen.
 */

import { supabase } from './supabase';
import { callEdgeFunction } from './edgeFunction';

/**
 * Meldet einen Benutzer mit Benutzername und Passwort am System an.
 *
 * @param {string} username - Der eingegebene Benutzername (z. B. "theki_max")
 * @param {string} password - Das eingegebene Passwort
 * /**@param {string} [captchaToken] - Optionales Turnstile-Token zur Bot-Abwehr
 * @returns {Promise<{ data: { session: object|null, user: object|null }, error: Error|null }>}
 *   Ein Ergebnisobjekt mit Benutzerdaten und aktiver Sitzung oder einem aussagekräftigen Fehler.
 */
export async function loginWithUsername(username, password, captchaToken) {
  // Benutzernamen trimmen und in Kleinbuchstaben vereinheitlichen
  const normalized = String(username || '').trim().toLowerCase();

  try {
    // 1. Serverseitige Prüfung via Edge Function ausführen
    const result = await callEdgeFunction(
      'auth-login',
      {
        username: normalized,
        password,
        ...(captchaToken ? { captchaToken } : {}),
      },
      false, // authenticated=false, da vor dem Login noch keine Sitzung existiert
    );

    const session = result?.session;
    if (!session?.access_token || !session?.refresh_token) {
      return { data: { session: null, user: null }, error: new Error('Login fehlgeschlagen.') };
    }

    // 2. Erhaltene Sitzungstoken in den Supabase-Client übernehmen
    // Dadurch werden alle nachfolgenden Abfragen mit den Benutzerrechten (RLS) verknüpft.
    const { data, error } = await supabase.auth.setSession({
      access_token: session.access_token,
      refresh_token: session.refresh_token,
    });

    if (error) return { data: { session: null, user: null }, error };

    return { data: { session: data.session, user: data.user }, error: null };
  } catch (err) {
    return {
      data: { session: null, user: null },
      error: err instanceof Error ? err : new Error('Login fehlgeschlagen.'),
    };
  }
}
